import React, { useRef } from 'react';
import { cleanClass } from '@shared/utils/cleanClass.js';
import { isMultiline } from '@shared/utils/isMultiline.js';
import { randomId } from '@shared/utils/randomId.js';
import IconV2 from './v2/IconV2.jsx';
import Spinner from './Spinner.jsx';
import ResizableTextArea from './v2/ResizableTextArea.jsx';
import SuggestionsFloatingPane from './v2/SuggestionsFloatingPane.jsx';
import { FiX } from './react-icons/main';

const Input = React.forwardRef(({ id, className = '', style = {}, label, description, icon, iconPosition = 'left', iconClassName = '', iconTitle, iconShortcut, onIconClick, autoSize, maxHeight, autoFocus, autoComplete, disabled, invalid, describedBy, inputAriaLabel, type, placeholder, onFocus, onBlur, onChange, onKeyPress, onKeyDown, onKeyUp, value, defaultValue, maxLength, htmlSize, loading, suggestions, suggestionsAlignWith, rows, size = 'md', nullable, onClear, min, max, allowMultiline, ...props }, ref) => {
  const finalId = useRef(id || randomId());

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
  };

  const handleKeyUp = (e) => {
    onKeyUp?.(e);
  };

  const handlePaste = (e) => {
    if (!allowMultiline) {
      return;
    }
    const text = e?.clipboardData?.getData('text');
    if (isMultiline(text)) {
      e.preventDefault();
      e.stopPropagation();
      onChange?.({ ...e, target: { ...e.target, value: text } });
    }
  };

  if (type === 'textarea') {
    return (
      <div className={cleanClass(`comp-input ${description ? 'has-description' : ''}`)}>
        {label && <div className={'label'}>{label}</div>}
        {description && <div className={'description'}>{description}</div>}
        <div className={`wrapper textarea ${className} ${size ? `size-${size}` : ''}`} style={style} {...props}>
          {loading && <Spinner />}
          {loading && <div className={'loading-overlay'} />}
          <ResizableTextArea //
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-label={inputAriaLabel}
            aria-describedby={describedBy}
            className={`${className || ''} ${loading ? 'loading' : ''}`}
            style={style}
            id={id}
            autoFocus={autoFocus}
            onKeyPress={onKeyPress}
            autoComplete={autoComplete ?? 'off'}
            onFocus={onFocus}
            onBlur={onBlur}
            type={type}
            placeholder={placeholder || label || ''}
            spellCheck={true}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            value={value}
            defaultValue={defaultValue}
            maxLength={maxLength}
            ref={ref}
            autoSize={autoSize}
            maxHeight={maxHeight}
            rows={rows}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`comp-input comp-input-${finalId.current} ${description ? 'has-description' : ''}`}>
      {label && <div className={'label'}>{label}</div>}
      {description && <div className={'description'}>{description}</div>}
      <div className={cleanClass(`wrapper ${className} ${icon ? 'has-icon' : ''} ${disabled ? 'disabled' : ''} ${size ? `size-${size}` : ''}`)} style={style} {...props}>
        {icon && iconPosition === 'left' && <IconV2 icon={loading ? <Spinner /> : icon} title={iconTitle} className={iconClassName} shortcut={iconShortcut} onClick={onIconClick} />}
        {loading && <div className={'loading-overlay'} />}
        <input //
          className={`${finalId.current} ${loading ? 'loading' : ''}`}
          name={finalId.current}
          autoFocus={autoFocus}
          autoComplete={autoComplete ?? 'off'}
          disabled={disabled || loading}
          aria-invalid={invalid || undefined}
          aria-label={inputAriaLabel}
          aria-describedby={describedBy}
          type={type}
          placeholder={placeholder || label || ''}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          onKeyPress={onKeyPress}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onPaste={handlePaste}
          maxLength={maxLength}
          {...(defaultValue === undefined ? { value: value || '' } : { defaultValue })}
          ref={ref}
          size={htmlSize}
          min={min}
          max={max}
        />
        {nullable && !!value && <IconV2 icon={FiX} onClick={onClear} />}
        {icon && iconPosition === 'right' && <IconV2 icon={icon} title={iconTitle} shortcut={iconShortcut} className={iconClassName} onClick={onIconClick} />}
        {suggestions && <SuggestionsFloatingPane id={finalId.current} alignWith={suggestionsAlignWith} suggestions={suggestions} onSuggestionChange={onChange} />}
      </div>
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
